import { useState } from 'react';
import { toast } from 'react-hot-toast';

import { Patient, Appointment, UserProfile } from '../types';

interface BaseReportData {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  filters: Record<string, any>;
}

interface AtendimentosReportData extends BaseReportData {
  type: 'atendimentos';
  data: Appointment[];
}

interface PacientesReportData extends BaseReportData {
  type: 'pacientes';
  data: Patient[];
}

interface ProfissionaisReportData extends BaseReportData {
  type: 'profissionais';
  data: UserProfile[];
}

interface IndicadoresReportData extends BaseReportData {
  type: 'indicadores';
  data: {
    totalPatients: number;
    totalProfessionals: number;
    totalAppointments: number;
    totalMedicalRecords: number;
    appointmentsByStatus?: Record<string, number>;
  };
}

type ReportData = AtendimentosReportData | PacientesReportData | ProfissionaisReportData | IndicadoresReportData;

export type { 
  ReportData,
  AtendimentosReportData,
  PacientesReportData,
  ProfissionaisReportData,
  IndicadoresReportData
};

export function useReports() {
  const [loading, setLoading] = useState(false);

  const generateReport = async (reportData: ReportData) => {
    setLoading(true);
    
    try {
      // Simular geração de relatório
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Em uma implementação real, você geraria o relatório aqui
      console.log('Gerando relatório:', reportData);
      
      // Simular download do relatório
      const reportContent = generateReportContent(reportData);
      downloadReport(reportContent, `relatorio-${reportData.type}-${new Date().toISOString().split('T')[0]}.txt`);
      
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const generateReportContent = (reportData: ReportData): string => {
    const { type, dateRange } = reportData;
    
    let content = `RELATÓRIO DE ${type.toUpperCase()}\n`;
    content += `Período: ${new Date(dateRange.startDate).toLocaleDateString('pt-BR')} a ${new Date(dateRange.endDate).toLocaleDateString('pt-BR')}\n`;
    content += `Gerado em: ${new Date().toLocaleString('pt-BR')}\n\n`;
    
    switch (type) {
      case 'atendimentos':
        content += `Total de atendimentos: ${reportData.data.length}\n\n`;
        content += 'DETALHES DOS ATENDIMENTOS:\n';
        reportData.data.forEach((appointment, index) => {
          content += `${index + 1}. Data: ${new Date(appointment.date).toLocaleDateString('pt-BR')}\n`;
          content += `   Horário: ${appointment.time}\n`;
          content += `   Tipo: ${appointment.type}\n`;
          content += `   Status: ${appointment.status}\n\n`;
        });
        break;
        
      case 'pacientes':
        content += `Total de pacientes: ${reportData.data.length}\n\n`;
        content += 'LISTA DE PACIENTES:\n';
        reportData.data.forEach((patient, index) => {
          content += `${index + 1}. Nome: ${patient.name}\n`;
          content += `   CPF: ${patient.cpf}\n`;
          content += `   Telefone: ${patient.phone}\n`;
          content += `   Status: ${patient.status}\n\n`;
        });
        break;
        
      case 'profissionais':
        content += `Total de profissionais: ${reportData.data.length}\n\n`;
        content += 'LISTA DE PROFISSIONAIS:\n';
        reportData.data.forEach((professional, index) => {
          content += `${index + 1}. Nome: ${professional.name}\n`;
          content += `   Função: ${professional.role}\n`;
          content += `   Unidade: ${professional.unit}\n`;
          content += `   Status: ${professional.status}\n\n`;
        });
        break;
        
      case 'indicadores':
        content += 'INDICADORES GERAIS:\n';
        content += `Total de Pacientes: ${reportData.data.totalPatients}\n`;
        content += `Total de Profissionais: ${reportData.data.totalProfessionals}\n`;
        content += `Total de Agendamentos: ${reportData.data.totalAppointments}\n`;
        content += `Total de Prontuários: ${reportData.data.totalMedicalRecords}\n\n`;
        
        if (reportData.data.appointmentsByStatus) {
          content += 'AGENDAMENTOS POR STATUS:\n';
          Object.entries(reportData.data.appointmentsByStatus).forEach(([status, count]) => {
            content += `${status}: ${count}\n`;
          });
        }
        break;
    }
    
    return content;
  };

  const downloadReport = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    generateReport,
    loading
  };
}